import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { Coordinate } from "./coordinate.model";
import { FishingEvent } from "./fishing-event.model";
import { GroundfishDelivery } from "./groundfish-delivery.model";
import { HMSDelivery } from "./hms-delivery.model";
import { LocalStorageService } from "./local-storage.service";
import { SetErrorBlock } from "./set-error-block.model";
import { TrawlSet } from "./trawl-set.model";
import { TrawlDelivery } from "./trawl-delivery.model";
import { formatDate } from "@angular/common";
import { Trip } from "./trip.model";
import { OperatorUser } from "./operator-user.model";
import { Injectable } from "@angular/core";

/**
 * Various trip and set validation functionality.
 */
@Injectable({ providedIn: 'root' })
export class TripValidator {

  constructor(private dataSync: LocalStorageService) {

  }
  /**
   * Check all the fields of all sets and the current groundfish landing.
   * @param isDeliveryEdit Are we checking sets on the edited landing?
   * @returns Array of SetErrorBlock objects, each one representing an error/warning
   */
  errorWarningDialogContentGroundfish(isDeliveryEdit: boolean = false): SetErrorBlock[] {
    let errors: SetErrorBlock[] = [];
    let sets = isDeliveryEdit ? this.dataSync.edited_groundfish().sets : this.dataSync.user_trip().groundfishDelivery.sets;

    sets.forEach((set, i) => {
      //Call groundfish helper function on each set
      let errorItems = this.groundfishSetErrors(set, sets);
      if (errorItems.length > 0) errors.push(new SetErrorBlock(set.setNumber == '' ? 'No String/Set#' : set.setNumber, errorItems, i, set.fishingEventID));
      if (set.catchItems.length == 0) errors.push(new SetErrorBlock(set.setNumber == '' ? 'No String/Set#' : set.setNumber, ['Set has no catch recorded'], i, set.fishingEventID, false));
    });

    //If the delivery hasn't been created yet, we can ignore the rest and return
    if (!isDeliveryEdit && this.dataSync.user_trip().groundfishDelivery.isNewDelivery) return errors;

    let landing = isDeliveryEdit ? this.dataSync.edited_groundfish() : this.dataSync.user_trip().groundfishDelivery;
    let errorItems = this.groundfishLandingErrors(landing);

    if (this.fishTicketError(landing)) errors.push(new SetErrorBlock('Delivery', ['One or more sets for this delivery has retained catch but no Fish ticket(s) are attached'], -1, '', false))
    if (errorItems.length > 0) errors.push(new SetErrorBlock('Delivery', errorItems, -1, ''));

    let logbooks = isDeliveryEdit ? this.dataSync.groundfish_logbooks().filter(rtlb => rtlb.localLandingGUID != landing.localLandingGUID)
                    : this.dataSync.groundfish_logbooks();
    let prevTickets = landing.tickets.filter(lnd => logbooks.some(gfl => gfl.tickets.some(tk => tk.ticketNumber == lnd.ticketNumber)));

    if (prevTickets.length > 0) 
      errors.push(new SetErrorBlock('Delivery', [`Fish ticket(s) ${prevTickets.map(pt => pt.ticketNumber).join(', ')} is associated with a previous delivery`], -1, '', false));

    return errors;
  }


  /**
 * Check all the fields of all sets and the current hms landing.
 * @param isDeliveryEdit Are we checking sets on the edited landing?
 * @returns Array of SetErrorBlock objects, each one representing an error/warning
 */
  errorWarningDialogContentHMS(isDeliveryEdit: boolean = false, draftIndex: number = -1): SetErrorBlock[] {
    let errors: SetErrorBlock[] = [];
    let sets = isDeliveryEdit ? this.dataSync.edited_hms().sets : draftIndex != -1 ? this.dataSync.hms_drafts()[draftIndex].sets : this.dataSync.user_trip().hmsDelivery.sets    

    //If species in set(s) are not in unloadings, provide a warning
    sets.forEach((set, i) => {
      //Call HMS helper function on each set
      let errorItems = this.hmsSetErrors(set);
      let ind = this.dataSync.user_trip().sets.findIndex(fe => set.fishingEventID == fe.fishingEventID);
      ind = isDeliveryEdit ? this.dataSync.edited_hms().sets.findIndex(fe => set.fishingEventID == fe.fishingEventID) : ind;

      if (errorItems.length > 0) errors.push(new SetErrorBlock(!set.startDate ? 'No Fishing Date' : formatDate(set.startDate, 'longDate', 'en-US'), errorItems, ind, set.fishingEventID));
      if (set.catchItems.length == 0 && set.hoursFished > 0) errors.push(new SetErrorBlock(!set.startDate ? 'No Fishing Date' : formatDate(set.startDate, 'longDate', 'en-US'), ['Set has no catch recorded'], ind, set.fishingEventID, false));
    });

    //If the delivery hasn't been created yet, we can ignore the rest and return
    if (!isDeliveryEdit && draftIndex == -1 && this.dataSync.user_trip().hmsDelivery.isNewDelivery) return errors; //does isNewDelivery function on hms delivery?? need to confirm

    let landing = isDeliveryEdit ? this.dataSync.edited_hms() : draftIndex != -1 ? this.dataSync.hms_drafts()[draftIndex] : this.dataSync.user_trip().hmsDelivery;
    let errorItems = this.hmsLandingErrors(landing);
    
    let retainedSpeciesInSets = sets.flatMap(s => s.catchItems)
                                    .filter(f => (f.retainedCount ?? 0) > 0 || (f.retainedWeight ?? 0) > 0)
                                    .map(ci => ci.species.name);
    let unloadingSpecies = landing.unloading.flatMap(ul => ul.species).map(s => s.speciesName);
    let speciesDiff = retainedSpeciesInSets.filter(sp => !unloadingSpecies.some(us => us == sp)).reduce((curr, acc) => curr + ', ' + acc, '');
    if (speciesDiff.length > 0) {
      errors.push(new SetErrorBlock('Delivery', ['Some retained species were not included in any unloadings: ' + speciesDiff], -1, '', false))
    }

    if (this.hmsUnloadingError(landing)) errors.push(new SetErrorBlock('Delivery', ['One or more sets for this delivery has retained catch but no Unloading(s) are attached'], -1, '', false))
    if (errorItems.length > 0) errors.push(new SetErrorBlock('Delivery', errorItems, -1, ''));

    return errors;
  }

  /**
 * Check all the fields of all sets and the current trawl landing.
 * @param isDeliveryEdit Are we checking sets on the edited landing?
 * @returns Array of SetErrorBlock objects, each one representing an error/warning
 */
  errorWarningDialogContentTrawl(isDeliveryEdit: boolean = false, draftIndex: number = -1): SetErrorBlock[] {
    let errors: SetErrorBlock[] = [];
    let delivery = isDeliveryEdit ? this.dataSync.edited_trawlLogbook() : draftIndex != -1 ? this.dataSync.trawl_drafts()[draftIndex] : this.dataSync.user_trawlTrip().trawlDelivery;

    delivery.sets.forEach((set, i) => {
      //Call groundfish helper function on each set
      let errorItems = this.trawlSetErrors(set, delivery, draftIndex);
      if (errorItems.length > 0) errors.push(new SetErrorBlock(!set.startDate ? 'No Haul Start' : formatDate(set.startDate, 'longDate', 'en-US'), errorItems, i, set.fishingEventID));
      
      let warnings = [];
      if (set.catchItems.length == 0) warnings.push('Set has no catch recorded');
      if (set.lostNet) warnings.push('You\'ve selected lost net, meaning that your entire net was lost. If this is correct, please disregard this warning.'); 
      
      if (warnings.length > 0) 
        errors.push(new SetErrorBlock(!set.startDate ? 'No Haul Start' : formatDate(set.startDate, 'longDate', 'en-US'), warnings, i, set.fishingEventID, false));
    });

    //If the delivery hasn't been created yet, we can ignore the rest and return
    if (!isDeliveryEdit && this.dataSync.user_trawlTrip().trawlDelivery.isNewDelivery) return errors;

    let errorItems = this.trawlLandingErrors(delivery);

    let deliveryWarnings = [];
    if (!delivery.atSeaDelivery && this.fishTicketError(delivery))  deliveryWarnings.push('One or more sets for this delivery has retained catch but no Fish ticket(s) are attached');    

    let logbooks = isDeliveryEdit ? this.dataSync.received_trawlLogbooks().filter(rtlb => rtlb.localLandingGUID != delivery.localLandingGUID)
                    : this.dataSync.received_trawlLogbooks();
    let prevTickets = delivery.tickets.filter(lnd => logbooks.some(gfl => gfl.tickets.some(tk => tk.ticketNumber == lnd.ticketNumber)));

    if (prevTickets.length > 0) {
      let multiple = prevTickets.length > 1;
      deliveryWarnings.push(`Fish ticket${multiple ? 's' : ''} ${prevTickets.map(pt => pt.ticketNumber)} ${multiple ? 'are' : 'is'} associated with a previous delivery`);
    }
    //Check trip depart/return - Trip must not overlap with date range of existing trip whether it is a completed one or a draft
    let completedTripOverlap = this.dataSync.received_trawlLogbooks().filter(rtlb => rtlb.localLandingGUID != (delivery.localLandingGUID ?? ''))
      .filter(lb => lb.departureDate.valueOf() < (new Date(delivery.returnDate ?? 0)).valueOf() &&
        (new Date(delivery.departureDate ?? 0)).valueOf() < lb.returnDate.valueOf());

    let draftTripOverlap = this.dataSync.trawl_drafts().filter((_, i) => draftIndex != i)
      .filter(lb => lb.departureDate.valueOf() < (new Date(delivery.returnDate ?? 0)).valueOf() &&
      (new Date(delivery.departureDate ?? 0)).valueOf() < lb.returnDate.valueOf());
    
    if (completedTripOverlap.length > 0)
      errorItems.push(`Trip date/time range overlaps with prior completed trip ${completedTripOverlap[0].logbookId.substring(17)}`);

    if (draftTripOverlap.length > 0)
      errorItems.push(`Trip date/time range overlaps with existing draft trip.`);

    if (deliveryWarnings.length > 0) errors.push(new SetErrorBlock('Delivery', deliveryWarnings, -1, '', false))
    if (errorItems.length > 0) errors.push(new SetErrorBlock('Delivery', errorItems, -1, ''));

    return errors;
  }

  /**
* Check all the fields of only sets, used on main menu and set selection pages
* @returns Array of SetErrorBlock objects, each one representing an error/warning
*/
  errorWarningDialogContent(user_trip: Trip, current_user: OperatorUser): SetErrorBlock[] {
    let errors: SetErrorBlock[] = [];
    let sets = user_trip.sets;

    if (current_user.groundfishFishery && !current_user.hmsFishery)
      sets = sets.filter(set => set.fishery == 'Groundfish');
    else if (current_user.hmsFishery && !current_user.groundfishFishery)
      sets = sets.filter(set => set.fishery == 'HMS');

    //For each set, check for required fields and specific requirements
    sets.forEach((set, i) => {

      //Loop through each set and call the respective functions for Groundfish/HMS
      let errorItems: string[] = set.fishery === "Groundfish" ? this.groundfishSetErrors(set, sets as FishingEvent[]) : this.hmsSetErrors(set);

      let setNum = set.setNumber == '' ? 'No String/Set#' : set.setNumber;
      let setDate = set.startDate ? formatDate(set.startDate, 'longDate', 'en-US') : 'No Fishing Date';

      if (errorItems.length > 0)
        errors.push(new SetErrorBlock(set.fishery == 'Groundfish' ? setNum : setDate, errorItems, i, set.fishingEventID));

      //Warning -- set has no recorded catch
      if (set.catchItems.length == 0 && (set.fishery == 'Groundfish' || set.hoursFished > 0)) 
        errors.push(new SetErrorBlock(set.fishery == 'Groundfish' ? setNum : setDate, ['Set has no catch recorded'], i, set.fishingEventID, false));
    });

    return errors;
  }

 /**
* Check all the fields of only trawl sets, used on main menu and set selection pages
* @returns Array of SetErrorBlock objects, each one representing an error/warning
*/
  trawlErrorWarningDialogContent(): SetErrorBlock[] {
    let errors: SetErrorBlock[] = [];
    let sets = this.dataSync.user_trawlTrip().sets;

    //For each set, check for required fields and specific requirements
    sets.forEach((set, i) => {

      //Loop through each set and call the respective functions for Groundfish/HMS
      let errorItems: string[] = [];
      errorItems = this.trawlSetErrors(set, this.dataSync.user_trawlTrip().trawlDelivery, -1)

      if (errorItems.length > 0) errors.push(new SetErrorBlock(!set.startDate ? 'No Haul Start' : formatDate(set.startDate, 'longDate', 'en-US'), errorItems, i, set.fishingEventID));

      //Warning -- set has no recorded catch
      if (set.catchItems.length == 0) errors.push(new SetErrorBlock(!set.startDate ? 'No Haul Start' : formatDate(set.startDate, 'longDate', 'en-US'), ['Set has no catch recorded'], i, set.fishingEventID, false));
    });

    return errors;

  }

  /**
   * Returns an array of error messages indicating validation issues found in a groundfish fishing event set.
   * @param set The fishing event set to validate.
   * @param sets All fishing event sets to check for duplicate set numbers.
   * @returns An array of error messages describing validation failures.
   */  
  private groundfishSetErrors(set: FishingEvent, sets: FishingEvent[]): string[] {
    let errorItems = [];

    if (set.startDate) {
      if (new Date(set.startDate).toISOString() > new Date().toISOString()) errorItems.push('Set date/time cannot be greater than current date/time');
    } else {
      errorItems.push('Set date missing');
    }

    if (set.endDate) {
      if (new Date(set.endDate).toISOString() > new Date().toISOString()) errorItems.push('Haul date/time cannot be greater than current date/time');
      if (set.startDate && (new Date(set.endDate).toISOString() <= new Date(set.startDate).toISOString())) errorItems.push('Haul date/time cannot be before or equal to Set date/time');
    } else {
      errorItems.push('Haul date missing');
    }

     if (!set.startLat.decimal) errorItems.push('Set Latitude missing');
     if (!set.startLon.decimal) errorItems.push('Set Longitude missing');
     if ((set.startLatManualEntry || set.startLonManualEntry) && this.coordinatesOutsideShapefile(set.startLat, set.startLon))
       errorItems.push('Manually entered Set coordinates are outside the valid area');

     if (!set.endLat?.decimal) errorItems.push('Haul Latitude missing');
     if (!set.endLon?.decimal) errorItems.push('Haul Longitude missing');
     if ((set.endLatManualEntry || set.endLonManualEntry) && this.coordinatesOutsideShapefile(set.endLat, set.endLon))
       errorItems.push('Manually entered Haul coordinates are outside the valid area');


    if (set.gear.type == 'Pelagic') {
      //Only if attached gear is pelagic
      if (set.depthOffBottom || set.depthOffBottom == 0) {
        if (set.depthOffBottom < 0) errorItems.push('Depth off bottom must be 0 or above');
      } else if (!set.depthOffBottom) {
        errorItems.push('Depth off bottom missing');
      }
    } else {
      //Only check if attached gear is not pelagic
      if (set.startDepth || set.startDepth == 0) {
        if (set.startDepth < 3 || set.startDepth > 700) errorItems.push('Start depth must be between 3 and 700');
      } else {
        errorItems.push('Start depth missing');
      }

      if (set.endDepth || set.endDepth == 0) {
        if (set.endDepth < 3 || set.endDepth > 700) errorItems.push('End depth must be between 3 and 700');
      } else {
        errorItems.push('End depth missing');
      }

      if (!set.skateNum && set.skateNum != 0) {
        errorItems.push('# of Skates, Tubs, or Pots per set missing');
      }

      if (set.lostPotCount || set.lostPotCount == 0) {
        if (set.lostPotCount > set.skateNum) errorItems.push('Lost Skates/Tubs/Pots count cannot be greater than # of Skates/Tubs/Pots per set');
      } else {
        errorItems.push('Lost Skates, Tubs, or Pots count missing');
      }
    }

    if (set.baitType == '' || !set.baitType) errorItems.push('Bait type missing');

    if (!set.setNumber) {
      errorItems.push('String/Set # missing');
    } else if (sets.filter(s => s.setNumber == set.setNumber).length > 1) {
      errorItems.push('String/Set # is used on another set')
    }

    if (set.gear.type == '' || set.gear.gearID == '') errorItems.push('Set has no gear attached');

    return errorItems;
  }


  /**
   * Returns an array of error messages indicating validation issues found in a HMS fishing event set.
   * @param set The fishing event set to validate.
   * @returns An array of error messages describing validation failures.
   */  
  private hmsSetErrors(set: FishingEvent): string[] {
    let errorItems = [];

    if (set.startDate) {
      if (new Date(set.startDate).toISOString() > new Date().toISOString()) errorItems.push('Set date/time cannot be greater than current date/time');
    } else {
      errorItems.push('Fishing date missing');
    }

    if (!set.fishingMode) errorItems.push('Fishing mode missing');

    if ((set.hoursFished > 0 || set.catchItems.length) && !set.startLat.decimal) errorItems.push('Set Latitude required');

    if ((set.hoursFished > 0 || set.catchItems.length) && !set.startLon.decimal) errorItems.push('Set Longitude required');

    if (set.hoursFished != null && set.hoursFished != undefined && !isNaN(set.hoursFished)) {
      if (set.hoursFished > 24) errorItems.push('Hours fished must be  between 0 and 24');
      if (set.catchItems.length > 0 && set.hoursFished == 0) errorItems.push('Hours fished cannot be 0 if catch is recorded');
    } else {
      errorItems.push('Hours fished required');
    }

    if (set.gear.type == '') errorItems.push('Set has no gear attached');


    if (set.gear.type == 'Deep Set Buoy Gear') {

      if (set.gear.gearSubtype.name == '') {

        errorItems.push('Gear subtype not selected');

      } else {

        if (typeof set.hookCount === 'number' && !isNaN(set.hookCount)) {
          if (set.hookCount > 30) {
            errorItems.push('Number of hooks must be 30 or less');
          }
        } else {
          errorItems.push('Number of hooks missing');
        }

        if (typeof set.buoyCount === 'number' && !isNaN(set.buoyCount)) {
          if (set.buoyCount > 10) {
            errorItems.push('Number of buoys must be 10 or less');
          }
        } else {
          errorItems.push('Number of buoys missing');
        }
      }
    }



    if (set.gear.type == 'Harpoon' && (set.aircraftUsed == null || set.aircraftUsed == undefined)) errorItems.push('Aircraft used indicator not set');


    return errorItems;
  }


  private trawlSetErrors(set: TrawlSet, delivery: TrawlDelivery, draftIndex: number): string[] {
    let errorItems = [];

    let setStart = set.startDate ? new Date(!set.startDate ? 0 : set.startDate).toISOString() : "";
    let setEnd = set.endDate ? new Date(!set.endDate ? 0 : set.endDate).toISOString() : "";
    let deliveryDep = new Date(!delivery.departureDate || isNaN(delivery.departureDate.getTime()) ? 0 : delivery.departureDate).toISOString();
    let deliveryRet = new Date(!delivery.returnDate ? 0 : delivery.returnDate).toISOString();

    if (!set.startDate) errorItems.push('Haul start date missing');
    else if (setStart > deliveryRet || setStart < deliveryDep) errorItems.push('Haul start date must be between trip departure and return date');

    if (!set.endDate && !set.lostNet) errorItems.push('Haul end date missing');
    if (set.endDate && (setEnd > deliveryRet || setEnd < deliveryDep)) errorItems.push('Haul end date must be between trip departure and return date');
    
    if (set.startDate && set.endDate && (setEnd <= setStart)) errorItems.push('Haul date/time cannot be before or equal to Set date/time');

    if (!set.startLat.decimal) errorItems.push('Haul start Latitude missing');

    if (!set.startLon.decimal) errorItems.push('Haul start Longitude missing');

    if ((set.startLatManualEntry || set.startLonManualEntry) && this.coordinatesOutsideShapefile(set.startLat, set.startLon))
      errorItems.push('Manually entered Haul start coordinates are outside the valid area');

    if (!set.endLat.decimal && !set.lostNet) errorItems.push('Haul end Latitude missing');

    if (!set.endLon.decimal && !set.lostNet) errorItems.push('Haul end Longitude missing');

    if (!set.lostNet && (set.endLatManualEntry || set.endLonManualEntry) && this.coordinatesOutsideShapefile(set.endLat, set.endLon))
      errorItems.push('Manually entered Haul end coordinates are outside the valid area');

    if (!set.averageDepth && !set.lostNet) errorItems.push('Average depth missing');
    if (set.averageDepth && set.averageDepth > 700) errorItems.push('Average depth must be 700 or less');

    if (!set.codendCapacity && delivery.isEMTrip) errorItems.push('Codend capacity required for EM trips');

    if (set.codendCapacity && set.codendCapacity > 750000) errorItems.push('Codend capacity must be 750,000 or less');

    if (set.targetSpecies.name == '') errorItems.push('Target species missing');

    if (set.netType.name == '') errorItems.push('Net type missing');

    //Check catch properly filled if emtrip or not
    if (delivery.isEMTrip && set.catchItems.some(ci => ci.releasedWeight == null)) errorItems.push("Trip is marked as EM but set contains catch with missing discard weight");

    return errorItems;
  }


  /**
   * Returns an array of error messages indicating validation issues found in a groundfish delivery landing record.
   * @param landing The groundfish delivery landing record to validate.
   * @returns An array of error messages describing validation failures.
   */
  private groundfishLandingErrors(landing: GroundfishDelivery): string[] {

    let errorItems = [];

    if (!landing.operatorName) errorItems.push('Operator name missing');

    if (!landing.vessel.isValid()) errorItems.push('Vessel name/number missing');

    if (landing.permitType.name == '') errorItems.push('Permit Type missing');

    if (!landing.permitNumber) errorItems.push('Permit # missing');

    let todayMax = new Date();
    todayMax.setHours(23, 59, 59);
    let minReturnDate = new Date(landing.sets.map(s => s.endDate).filter(date => date != null && date != undefined)
                .reduce((curr, acc) => new Date(curr).getTime() > new Date(acc).getTime() ? curr : acc, new Date(0)));
   minReturnDate.setHours(0, 0, 0, 0);
    if (!landing.returnDate) errorItems.push('Landing date missing');
    else if (new Date(landing.returnDate).toISOString() > todayMax.toISOString()) 
      errorItems.push('Landing date/time cannot be greater than current date/time');
    else if (new Date(landing.returnDate).getTime() < new Date(minReturnDate).getTime()) 
      errorItems.push('Landing date/time must be greater or equal to latest attached set retrieval');


    if (!landing.returnPort.name) errorItems.push('Return port missing');

    if (landing.sets.length == 0) errorItems.push('Delivery requires at least one completed gear set/retrieval');

    // Check if any fish tickets have landing dates before the delivery return date
    if (landing.returnDate) {
      const returnDate = new Date(landing.returnDate);
      returnDate.setHours(0, 0, 0, 0);
      landing.tickets.forEach(ticket => {
        if (ticket.deliveryDate) {
          const ticketDate = new Date(ticket.deliveryDate);
          ticketDate.setHours(0, 0, 0, 0);
          if (ticketDate < returnDate) {
            errorItems.push(`Fish ticket ${ticket.ticketNumber} landing date is before the delivery return date`);
          }
        }
      });
    }

    return errorItems;
  }


  /**
   * Checks for errors in an HMS delivery landing record.
   * Validates operator name, vessel information, departure and arrival dates, ports, and the presence of completed sets.
   * @param landing The HMS delivery landing record to validate.
   * @returns An array of error messages indicating validation failures.
   */
  private hmsLandingErrors(landing: HMSDelivery): string[] {

    let errorItems = [];

    let landingArrDate = new Date(landing.arrivalDate);
    let landingDepDate = new Date(landing.departureDate);

    if (!landing.operatorName) errorItems.push('Operator name missing');

    if (!landing.vessel.isValid()) errorItems.push('Vessel name/number missing');

    if (!landing.departureDate) errorItems.push('Departure date missing');
    
    let todayMax = new Date();
    todayMax.setHours(23, 59, 59);

    if (!landing.arrivalDate) errorItems.push('Arrival date missing');
    else if (landingArrDate.toISOString() > todayMax.toISOString()) errorItems.push('Arrival date cannot be greater than current date');
    

    if (landing.departureDate && landing.arrivalDate && (landingDepDate.toISOString() > landingDepDate.toISOString())) {
      errorItems.push('Departure date cannot be greater than arrival date');
    }

    if (!landing.departurePort.name) errorItems.push('Departure port missing');

    if (!landing.arrivalPort.name) errorItems.push('Arrival port missing');


    if (landing.sets.length == 0) {
      errorItems.push('Delivery requires at least one completed set');
    } else if (landing.arrivalDate && landing.departureDate) {
      errorItems = errorItems.concat(landing.sets.filter(set => set.startDate && (new Date(set.startDate).getTime() < landingDepDate.getTime()) || new Date(set.startDate).getTime() > landingArrDate.getTime())
      .map(s => `Set on ${s.startDate} is outside trip departure and arrival date`));
    }
      
    return errorItems;
  }

  /**
     * Returns an array of error messages indicating validation issues found in a trawl delivery landing record.
     * @param delivery The trawl delivery landing record to validate.
     * @returns An array of error messages describing validation failures.
     */
  private trawlLandingErrors(delivery: TrawlDelivery): string[] {
    let errorItems = [];

    let landingArrDate = new Date(delivery.returnDate);

    if (!delivery.operatorName) errorItems.push('Operator name missing');

    if (!delivery.vessel.isValid()) errorItems.push('Vessel name/number missing');

    if (!delivery.crewSize) errorItems.push('Crew size missing');

    let todayMax = new Date();
    todayMax.setHours(23, 59, 59);

    if (!delivery.returnDate) {
      errorItems.push('Return date missing');
    } else if (landingArrDate.toISOString() > todayMax.toISOString()) {
      errorItems.push('Return date/time cannot be greater than current date/time');
    }

    if (!delivery.departureDate) {
      errorItems.push('Departure date missing');
    } else if (delivery.returnDate && new Date(isNaN(delivery.departureDate.getTime()) ? 0 : delivery.departureDate).toISOString() > landingArrDate.toISOString()) {
      errorItems.push('Departure date/time cannot be greater than return date/time');
    }

    if (!delivery.returnPort.name) errorItems.push('Return port missing');
    if (!delivery.departurePort.name) errorItems.push('Departure port missing');

    if (delivery.atSeaDelivery && !delivery.mothershipName) errorItems.push("Mothership name missing for at-sea delivery");

    if (delivery.sets.length == 0)
      errorItems.push('Delivery requires at least one completed gear set/retrieval');

    // Check if any fish tickets have landing dates before the delivery return date
    if (delivery.returnDate) {
      const returnDate = new Date(delivery.returnDate);
      returnDate.setHours(0, 0, 0, 0);
      delivery.tickets.forEach(ticket => {
        if (ticket.deliveryDate) {
          const ticketDate = new Date(ticket.deliveryDate);
          ticketDate.setHours(0, 0, 0, 0);
          if (ticketDate < returnDate) {
            errorItems.push(`Fish ticket ${ticket.ticketNumber} landing date is before the delivery return date`);
          }
        }
      });
    }

    return errorItems;
  }

  /**
   * Checks if the groundfish delivery landing requires a fish ticket due to retained catch on at least one of its sets.
   * @param landing The groundfish delivery landing to evaluate.
   * @returns `true` if the landing has retained catch on at least one set and no fish ticket is attached; otherwise, `false`.
   */
  private fishTicketError(landing: GroundfishDelivery | TrawlDelivery): boolean {
    return (landing.sets.some(set => set.catchItems.some(ct => ct.releasedWeight ?? 0 > 0)) && landing.tickets.length == 0);
  }


  /**
   * Checks if the HMS delivery requires unloading due to retained catch on at least one of its sets.
   * @param landing The HMS delivery landing to evaluate.
   * @returns `true` if the landing has retained catch on at least one set and no unloading is attached; otherwise, `false`.
   */
  private hmsUnloadingError(landing: HMSDelivery): boolean {
    return landing.sets.some(set => set.catchItems.some(ct => ct.retainedWeight ?? 0 > 0)) && landing.unloading.length == 0;
  }

  /**
   * Checks whether a lat/lon pair falls outside the West Coast EEZ shapefile.
   * Mirrors {@link shapefileLatValidator}/{@link shapefileLonValidator}: unfilled coordinates
   * are treated as valid (their absence is reported by the dedicated "missing" checks).
   * @param lat The latitude coordinate.
   * @param lon The longitude coordinate.
   * @returns `true` if both coordinates are filled and the point lies outside the shapefile; otherwise `false`.
   */
  private coordinatesOutsideShapefile(lat: Coordinate, lon: Coordinate): boolean {
    const west_coast_eez = this.dataSync.west_coast_eez();
    if (!lat || !lon || lat.isUnfilled() || lon.isUnfilled() || !shapefileLoaded(west_coast_eez)) return false;
    return !booleanPointInPolygon([lon.decimal, lat.decimal], west_coast_eez);
  }

}  

/**
 * Has the West Coast EEZ shapefile actually loaded? It starts as an empty polygon
 * ({@link LocalStorageService.west_coast_eez} = `{type:'Polygon', coordinates:[]}`) and is
 * populated asynchronously at app startup (see app.component). `booleanPointInPolygon` returns
 * `false` for EVERY point when tested against an empty polygon, so callers must skip the geofence
 * check until the data is present — otherwise valid coordinates are falsely reported as
 * "out of range". On an offline-first vessel app we never want a missing shapefile to block entry
 * of an otherwise-valid coordinate.
 * @param west_coast_eez The EEZ polygon/multipolygon geometry.
 * @returns `true` if the geometry has at least one ring of coordinates.
 */
export function shapefileLoaded(west_coast_eez: any): boolean {
    return !!west_coast_eez?.coordinates?.length;
}

/**
 * If latitude has been entered manually, it is subject to constraints. Check against shapefile.
 * @param manual Has the control value been entered manually?
 * @returns Validator function
 */
export function shapefileLatValidator(manual: boolean, lon: Coordinate, west_coast_eez: any): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const lat: Coordinate = control.value;
        if (!manual || lat.isUnfilled() || lon.isUnfilled() || !shapefileLoaded(west_coast_eez)) return null;
        return booleanPointInPolygon([lon.decimal, lat.decimal], west_coast_eez) ? null : {shpLatError: 'Coordinates outside valid area'};
    }
}

/**
 * If latitude has been entered manually, it is subject to constraints. Check against shapefile.
 * @param manual Has the control value been entered manually?
 * @returns Validator function
 */
export function shapefileLonValidator(manual: boolean, lat: Coordinate, west_coast_eez: any): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const lon: Coordinate = control.value;
        if (!manual || lat.isUnfilled() || lon.isUnfilled() || !shapefileLoaded(west_coast_eez)) return null;
        return booleanPointInPolygon([lon.decimal, lat.decimal], west_coast_eez) ? null : {shpLonError: 'Coordinates outside valid area'};
    }
}


/**
 * For validation on the set form, is the haul date after the set date?
 * @param startDate Set date
 * @returns Validator function
 */
export function setHaulDateValidator(startDate: Date): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return startDate && control.value && new Date(startDate).toISOString() >= new Date(control.value).toISOString() ? { setAfterHaul: true } : null;
    };
}

/**
 * Does the given control have a date not greater than current date?
 * @returns Validator function
 */
export function maxDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.value && new Date(control.value).toISOString() > new Date().toISOString() ? {maxDate: {value: control.value}} : null;
    };
}



/**
 * For gear fields on sets, this is to detect if it's empty.
 * @returns Validator function
 */
export function gearValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        return control.value.gearID != '' && control.value.type != '' ? null : {object: {error: 'Empty value'}};
    }
}

/**
 * Detect if coordinate fields are empty.
 * @returns Validator
 */
export function coordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        return control.value.decimal ? null : {object: {error: 'Empty value'}};
    }
}

/**
 * Detect if the given fish ticket number follows the correct structure: <b>^([0-9]{6}[a-zA-Z]|[0-9]{8}|[a-zA-Z]{2}[0-9]{6})$</b>
 * @returns Validator function
 */
export function fishTicketValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
      const format = new RegExp('^([0-9]{6}[a-zA-Z]|[0-9]{8}|[0-9]{7}|[a-zA-Z]{2}[0-9]{6})$');
      return format.test(control.value) ? null : {ticket: {error: 'Invalid format. Must be one of: 123456E, 12345678, 1234567, or AB123456'}};
  }
}

/**
 * Detect if the given landing receipt number follows the correct structure: 
 * where the ticket number is exactly 7 or 8 characters long and follows the pattern XX0000X or XX00000X.
 * Allows for empty fields to pass validation.
 * @returns Validator function
 */
export function unloadingReceiptValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    // Regex for exactly 7 or 8 characters: XX0000X or XX00000X
    let format = new RegExp('^[a-zA-Z0-9]{2}[0-9]{4}[a-zA-Z0-9]$|^[a-zA-Z0-9]{2}[0-9]{5}[a-zA-Z0-9]$');
    return !control.value || format.test(control.value) ? null : { ticket: { error: 'Invalid format, must be XX0000X or XX00000X, X may be a letter or number.' } };
  };
}

/**
 * Used in dropdowns/autocompletes, checks for empty field expecting a lookup item.
 * @returns Validator function
 */
export function lookupItemValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        return !control.value.lookupType || control.value.name == "" ? {value: {error: 'Select an item'}} : null;
    }
}

/**
 * Check for complete vessel information on delivery-detail page.
 * @returns Validator function
 */
export function vesselDataValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        return !control.value || control.value.vesselName == "" || control.value.vesselCoastNumber == "" ? {value: {error: 'Select an item'}} : null;
    }
}

/**
 * Check if entered fish ticket matches a number already present on the delivery.
 * @param ticketNums Fish ticket numbers already recorded on the given delivery
 * @returns Validator function
 */
export function duplicateTicketValidator(ticketNums: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        return ticketNums.includes(control.value) ? {duplicate: {error: 'Ticket # already used on delivery'}} : null;
    }
}

/**
 * Disallow the number of lost pots to be greater than the number of skates.
 * Does not apply to sets with Pelagic gear types on them.
 * @returns Validator function
 */
export function skateNumLostPotValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const gearType = control.get('gear')?.value.type;
        if (gearType == 'Pelagic') return null;

        const skateNum = control.get('skateNum');
        const lostPot = control.get('lostPotNum');
        return Number.parseInt(skateNum?.value) >= Number.parseInt(lostPot?.value) ? null : {invalid: 'Lost amount must be less than skate/tub/pot amount'};
    }
}


/**
 * Ensure arrival date is after departure date on HMS delivery form
 * @returns Validator function
 */
export function arrivalAfterDepartureValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const departureDate = new Date(control.get('departureDate')?.value);
    const arrivalDate = new Date(control.get('arrivalDate')?.value);

    if (departureDate instanceof Date && !isNaN(departureDate.getTime()) && 
    arrivalDate instanceof Date && !isNaN(arrivalDate.getTime()) && arrivalDate.toISOString() < departureDate.toISOString()) {
      return { arrivalBeforeDeparture: true }; // Return validation error if not valid
    }
    return null; // Return null if validation passes
  }
}


/**
* Species validator
*/
export function speciesValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const species = control.value;
    // Check if species is an object and its name property is defined and not just whitespace.
    if (species && typeof species === 'object' && species.name && species.name.trim().length > 0) {
      return null;
    }
    return { speciesInvalid: true };
  };
}
